import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Fab, InputBase, Link, List, ListItem, ListItemText, Paper, TextField } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { Delete, Person } from '@material-ui/icons'
import { useSnackbar } from 'notistack'
import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'

import { Collections } from 'Collections'
import { useAppUser } from 'components/AppUserProvider'
import { FullscreenLoading } from 'components/FullscreenLoading'
import { ImageButton } from 'components/ImageButton'
import { useOrg, useUnselectOrg } from 'components/OrgProvider'
import { useOrgUser } from 'components/OrgUserProvider'
import { PageNavigation } from 'components/PageNavigation'
import { ProfileSaveButton } from 'components/ProfileSaveButton'
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils'
import { useSyncedState } from 'hooks/useSyncedState'
import { AnalyticsEvent } from 'models/AnalyticsEvent'
import { avatarImageFrom } from 'models/AvatarImage'
import { contactInfoFrom } from 'models/ContactInfo'
import { emailFrom } from 'models/Email'
import { Name, nameFrom } from 'models/Name'
import { Nullable } from 'models/Nullable'
import { OrgUser } from 'models/OrgUser'
import { phoneFrom } from 'models/Phone'
import { Paths } from 'Paths'
import { dateFormat } from 'Util/date.utils'
import { handleEnterKey } from 'Util/handleEnterKey'

import { OrgUserRolePicker } from './Users/OrgUserScreen'

type NameModalProps = {
    onClose: (value: Nullable<Name>) => void
    title: string
    onCancel: () => void
    value: Nullable<Name>
}

export function NameModal({ onClose, onCancel, title, value }: NameModalProps) {
    const [firstName, setFirstName] = useState(value?.firstName || '')
    const [lastName, setLastName] = useState(value?.lastName || '')

    //TODO: Validation
    const isValid = true

    function onComplete() {
        if (isValid) {
            onClose(nameFrom(
                firstName,
                lastName,
            ))
        }
    }

    const lastNameRef = useRef<any>()

    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <List>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            placeholder="Fornavn"
                            autoFocus
                            fullWidth
                            onKeyPress={handleEnterKey(() => lastNameRef.current?.focus())}
                        />
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            placeholder="Etternavn"
                            fullWidth
                            ref={lastNameRef}
                            onKeyPress={handleEnterKey(onComplete)}

                        />
                    </ListItem>
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Avbryt</Button>
                <Button disabled={!isValid} onClick={onComplete} color="primary">{'Ok'}</Button>
            </DialogActions>
        </Dialog>
    )
}

type NameInputProps = {
    value: Nullable<Name>
    onChange: (value: Nullable<Name>) => void
}

export function NameInput({ value, onChange }: NameInputProps) {
    const [show, setShow] = useState(false)
    return (
        <>
            <InputBase
                value={value?.fullName || ''}
                type="text"
                placeholder="Navn"
                fullWidth
                onClick={() => setShow(true)}
                onChange={() => setShow(true)}
                inputProps={{
                    'aria-label': 'first name input',
                    style: {
                        fontSize: 26,
                        fontWeight: 'bold',
                        textAlign: 'center',
                    },
                }}
            />
            {show ? (
                <NameModal
                    title="Navn"
                    value={value}
                    onCancel={() => setShow(false)}
                    onClose={(newAddress) => {
                        onChange(newAddress)
                        setShow(false)
                    }}
                />
            ) : null}
        </>
    )
}

export function MyOrgUserScreen() {
    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory()
    const [orgDoc, loadingOrg] = useOrg()
    const [unselectOrg] = useUnselectOrg()
    const [appUserDoc, loadingAppUser] = useAppUser()
    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const org = orgDoc?.data || null
    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const [showConfirmation, setShowConfirmation] = useState(false)
    const [showDeleteUser, setShowDeleteUser] = useState(false)

    const [name, setName] = useSyncedState(orgUser?.orgContactInfo?.name || null)
    const [phoneNumber, setPhoneNumber] = useSyncedState(orgUser?.orgContactInfo?.phone?.phoneNumber || '')
    const [orgUserEmail, setOrgUserEmail] = useSyncedState(orgUser?.orgContactInfo?.email?.email || '')
    const [avatarImageUrl, setAvatarImageUrl] = useSyncedState(orgUser?.avatarImage?.original.url || null)

    const isOrgOwner = orgUser?.userId === org?.owner.appUser?.id
    const loading = loadingOrgUser || loadingOrg || loadingAppUser

    const hasChanges = name?.fullName !== orgUser?.orgContactInfo?.name?.fullName
        || phoneNumber !== (orgUser?.orgContactInfo?.phone?.phoneNumber || '')
        || orgUserEmail !== (orgUser?.orgContactInfo?.email?.email || '')
        || avatarImageUrl !== (orgUser?.avatarImage?.original.url || null)

    //Todo: validation
    const isValid = hasChanges
    function save() {
        if (!orgUser) {
            console.error('No orgUser state!')
            return
        }
        if (!appUser) {
            console.error('No appUser state!')
            return
        }
        if (isValid) {
            FirebaseUtils.updateDocument<OrgUser>(
                true,
                Collections.ORG_USERS,
                orgUser.id,
                appUser,
                orgUser,
                AnalyticsEvent.UPDATED_OWN_ORG_USER,
                AnalyticsEvent.UPDATED_OWN_ORG_USER,
                AnalyticsEvent.UPDATE_OWN_ORG_USER_ERROR,
                enqueueSnackbar,
                'Vellykket!',
                {
                    orgContactInfo: contactInfoFrom(
                        name,
                        phoneFrom(phoneNumber),
                        emailFrom(orgUserEmail),
                    ),
                    avatarImage: avatarImageFrom(avatarImageUrl),
                },
            )

        }
    }

    const content = loading ? <FullscreenLoading screenId="My Org User" /> : (
        (!orgUser || !orgUserDoc || !appUser) ? (
            <List>
                <ListItem>
                    <ListItemText primary="Kunne ikke finne brukeren" />
                </ListItem>
            </List>
        ) : (
            <List>
                <ListItem>
                    <Box mx="auto" mt="40px">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Paper
                                elevation={3}
                                style={{
                                    position: 'relative',
                                    //width: 300,
                                    //height: 300,
                                    borderRadius: 18,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Fab
                                    size="medium"
                                    style={{ position: 'absolute', bottom: 40, right: -16 }}
                                    onClick={() => setShowDeleteUser(true)}
                                    color="secondary"
                                    aria-label="delete"
                                >
                                    <Delete />
                                </Fab>
                                <ImageButton
                                    label="User Avatar Image"
                                    value={avatarImageUrl}
                                    onChange={setAvatarImageUrl}
                                >
                                    <Person />
                                </ImageButton>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        marginTop: 18,
                                        marginBottom: 18,
                                    }}
                                >
                                    <NameInput
                                        value={name}
                                        onChange={setName}
                                    />
                                    <InputBase
                                        value={phoneNumber}
                                        placeholder="Arbeids tlf"
                                        onChange={e => setPhoneNumber(e.target.value)}
                                        type="number"
                                        inputProps={{
                                            'aria-label': 'phone number input',
                                            style: {
                                                fontSize: 16,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                    <InputBase
                                        value={orgUserEmail}
                                        placeholder="Arbeids E-post"
                                        onChange={e => setOrgUserEmail(e.target.value)}
                                        inputProps={{
                                            'aria-label': 'work email input',
                                            style: {
                                                fontSize: 16,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                    <Typography variant="subtitle2">Opprettet: {dateFormat.format(orgUserDoc.metadata.createdAt.toDate())}</Typography>
                                    <Box mt={2}>
                                        <Link component={RouterLink} to={Paths.MY_APP_USER}>Gå til App bruker</Link>
                                    </Box>
                                </div>
                                <ProfileSaveButton
                                    hasChanges={hasChanges}
                                    save={save}
                                    isValid={isValid}
                                />
                            </Paper>
                        </div>
                    </Box>
                </ListItem>
                {(!loading) ? <OrgUserRolePicker orgUser={orgUser} ownAppUser={appUser} ownOrgUser={orgUser} isOrgOwner={isOrgOwner} isOwnUser /> : null}
            </List>
        )
    )


    return (
        <PageNavigation
            title="Min Org-Bruker"
            hasUnsavedChanges={hasChanges}
        >
            {showDeleteUser ? (
                <Dialog
                    open
                    onClose={() => {
                        setShowDeleteUser(false)
                        setShowConfirmation(false)
                    }}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Fjern bruker"}</DialogTitle>
                    <DialogContent>
                        <Typography variant="subtitle1" display="inline">
                            {
                                isOrgOwner
                                    ? 'Du kan ikke forlate din egen organisasjon. Hvis du ønsker å slette organisasjonen din kan du gjøre det '
                                    : (showConfirmation
                                        ? 'Er du sikker? Dette kan ikke angres!'
                                        : 'Ønsker du å fjerne brukeren fra organisasjonen? Dette kan ikke angres')
                            }
                        </Typography>
                        {isOrgOwner ? (
                            <Link component={RouterLink} to={Paths.MY_ORG}>her</Link>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        {isOrgOwner ? null : (showConfirmation ? (
                            <Button
                                color="secondary"
                                disabled={loading || isOrgOwner}
                                onClick={() => {
                                    if (orgUser?.id && !isOrgOwner && appUser) {
                                        FirebaseUtils.deleteDocument(
                                            true,
                                            Collections.ORG_USERS,
                                            orgUser.id,
                                            appUser,
                                            orgUser,
                                            AnalyticsEvent.DELETED_ORG_USER,
                                            AnalyticsEvent.DELETED_OWN_ORG_USER,
                                            AnalyticsEvent.DELETE_ORG_USER_ERROR,
                                            enqueueSnackbar,
                                            'Forlot organisasjonen',
                                            doc => ({
                                                orgId: org?.id,
                                                isOrgOwner,
                                                isOwnUser: true,
                                                orgUserId: orgUser?.id,
                                                appUserId: orgUser?.userId,
                                            })
                                        )
                                        history.push(Paths.DASHBOARD)
                                        unselectOrg(AnalyticsEvent.DELETED_OWN_ORG_USER)
                                    }
                                }}
                            >Ja</Button>
                        ) : (
                            <Button
                                color="secondary"
                                onClick={() => setShowConfirmation(true)}
                                disabled={loading || isOrgOwner}
                            >
                                Forlat organisasjonen
                            </Button>
                        ))}
                        <Button
                            color="primary"
                            onClick={() => {
                                setShowDeleteUser(false)
                                setShowConfirmation(false)
                            }}
                        >{isOrgOwner ? 'Ok' : 'Avbryt'}</Button>
                    </DialogActions>
                </Dialog>
            ) : null
            }
            {content}
        </PageNavigation >
    )
}
